import { create } from "zustand";
import { profileApi, type Profile } from "../lib/api";

interface ProfileState {
  profile: Profile | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchProfile: (token: string) => Promise<void>;
  updateProfile: (token: string, data: { name?: string; bio?: string }) => Promise<Profile>;
  uploadAvatar: (token: string, file: File) => Promise<string>;
  deleteAvatar: (token: string) => Promise<void>;
  clearProfile: () => void;
}

export const useProfileStore = create<ProfileState>()((set) => ({
  profile: null,
  isLoading: false,
  error: null,

  fetchProfile: async (token: string) => {
    set({ isLoading: true, error: null });
    try {
      const profile = await profileApi.get(token);
      set({ profile, isLoading: false });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to load profile",
      });
    }
  },

  updateProfile: async (token: string, data: { name?: string; bio?: string }) => {
    set({ isLoading: true, error: null });
    try {
      const profile = await profileApi.update(token, data);
      set({ profile, isLoading: false });
      return profile;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to update profile";
      set({ isLoading: false, error: errorMsg });
      throw err;
    }
  },

  uploadAvatar: async (token: string, file: File) => {
    set({ isLoading: true, error: null });
    try {
      const { avatarUrl, updatedAt } = await profileApi.uploadAvatar(token, file);
      set((state) => ({
        profile: state.profile ? { ...state.profile, avatarUrl, updatedAt } : null,
        isLoading: false,
      }));
      return avatarUrl;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to upload avatar";
      set({ isLoading: false, error: errorMsg });
      throw err;
    }
  },

  deleteAvatar: async (token: string) => {
    set({ isLoading: true, error: null });
    try {
      await profileApi.deleteAvatar(token);
      set((state) => ({
        profile: state.profile ? { ...state.profile, avatarUrl: null } : null,
        isLoading: false,
      }));
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to delete avatar";
      set({ isLoading: false, error: errorMsg });
      throw err;
    }
  },

  clearProfile: () => {
    set({ profile: null, isLoading: false, error: null });
  },
}));
